import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {BranchListResponseBody} from '@dms/api/shared';
import {
  GetDeliveryNoteResponse,
  GetWarehousesResponse,
  usePatchDeliveryNoteForCompletedMutation,
  usePatchDeliveryNoteMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromBranches,
  getOptionsFromWarehouses,
  handleApiError,
} from '@dms/shared';
import {getOptionsFromPaymentTypes} from '@dms/warehouse/shared';

import {ApiError, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  TransferDeliveryNoteForm,
  TransferDeliveryNoteFormSchema,
} from '../types/TransferDeliveryNoteForm';

export interface BranchBasedInformationFormProps extends RequiredTestIdProps {
  isCompleted: boolean;
  deliveryNote: GetDeliveryNoteResponse | Nullish;
  branchList: BranchListResponseBody | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes?: PaymentTypeResponseBody[] | Nullish;
  onClose: () => void;
}

export function BranchBasedInformationForm(props: BranchBasedInformationFormProps) {
  const [patchDeliveryNote] = usePatchDeliveryNoteMutation();
  const [patchDeliveryNoteForCompleted] = usePatchDeliveryNoteForCompletedMutation();

  const isOpeningBalance = props.deliveryNote?.type === 'OPENING_BALANCE';

  const defaultValues: Partial<TransferDeliveryNoteForm> = {
    sourceBranchId: props.deliveryNote?.transferSourceBranchId,
    branchId: props.deliveryNote?.branchId,
    authorizationProfileId: props.deliveryNote?.authorizationProfileId,
    warehouseId: props.deliveryNote?.warehouseId,
    deliveryNoteNumber: props.deliveryNote?.deliveryNoteNumber,
    issuedAt: parseDate(props.deliveryNote?.issuedAt),
    paymentType: props.deliveryNote?.paymentType,
    note: props.deliveryNote?.note,
  };

  const handleSubmit: FormSubmitHandler<TransferDeliveryNoteForm> = async (
    formValues,
    setErrors,
    reset,
    blockerContext
  ) => {
    if (isNil(props.deliveryNote)) {
      return;
    }

    if (props.isCompleted) {
      return await patchDeliveryNoteForCompleted({
        deliveryNoteId: props.deliveryNote.deliveryNoteId,
        body: {
          deliveryNoteNumber: formValues.deliveryNoteNumber,
          issuedAt: formValues.issuedAt?.toISOString(),
          note: formValues.note,
        },
      })
        .unwrap()
        .then(props.onClose)
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchDeliveryNote({
      deliveryNoteId: props.deliveryNote.deliveryNoteId,
      body: {
        authorizationProfileId: formValues.authorizationProfileId,
        warehouseId: formValues.warehouseId,
        deliveryNoteNumber: formValues.deliveryNoteNumber,
        issuedAt: formValues.issuedAt?.toISOString(),
        paymentType: formValues.paymentType,
        note: formValues.note,
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<TransferDeliveryNoteForm>
      experimentalZodSchema={TransferDeliveryNoteFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => (
        <>
          <VStack spacing={4}>
            <Grid columns={4}>
              <FormField
                isRequired
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="sourceBranchId"
                label={i18n.t('entity.warehouse.labels.sourceBranch')}
                options={getOptionsFromBranches(props.branchList)}
                data-testid={suffixTestId('inputs.sourceBranchId', props)}
              />
              <FormField
                isRequired
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="branchId"
                label={i18n.t('entity.warehouse.labels.branch')}
                options={getOptionsFromBranches(props.branchList)}
                data-testid={suffixTestId('inputs.branchId', props)}
              />
              <FormField
                isNotClearable
                isDisabled
                control={control}
                type="choice"
                name="authorizationProfileId"
                label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                options={getOptionsFromAuthorizationProfiles(
                  props.authorizationProfiles,
                  formApi.watch('authorizationProfileId')
                )}
                data-testid={suffixTestId('inputs.authorizationProfileId', props)}
              />
              <FormField
                isRequired
                isNotClearable
                isDisabled={props.isCompleted}
                control={control}
                type="choice"
                name="warehouseId"
                label={i18n.t('entity.warehouse.labels.warehouse')}
                options={getOptionsFromWarehouses(props.warehouses)}
                data-testid={suffixTestId('inputs.warehouseId', props)}
              />
            </Grid>
            <Grid columns={4}>
              <FormField
                isDisabled={!isOpeningBalance}
                control={control}
                type="text"
                name="deliveryNoteNumber"
                label={
                  isOpeningBalance
                    ? i18n.t('entity.warehouse.labels.openingBalanceNo')
                    : i18n.t('entity.warehouse.labels.transferOrderNo')
                }
                data-testid={suffixTestId('inputs.deliveryNoteNumber', props)}
              />
              <FormField
                isDisabled
                control={control}
                type="date"
                name="issuedAt"
                label={
                  isOpeningBalance
                    ? i18n.t('entity.warehouse.labels.openingBalanceDate')
                    : i18n.t('entity.warehouse.labels.transferOrderDate')
                }
                data-testid={suffixTestId('inputs.issuedAt', props)}
              />
              <FormField
                isRequired
                isNotClearable
                isDisabled={props.isCompleted}
                type="choice"
                name="paymentType"
                label={i18n.t('entity.warehouse.labels.paymentType')}
                options={getOptionsFromPaymentTypes(props.paymentTypes)}
                control={control}
                data-testid={suffixTestId('inputs.paymentType', props)}
              />
            </Grid>
            <Grid columns={4}>
              <GridItem span={4}>
                <FormField
                  control={control}
                  type="textarea"
                  name="note"
                  label={i18n.t('entity.warehouse.labels.note')}
                  data-testid={suffixTestId('inputs.note', props)}
                />
              </GridItem>
            </Grid>
          </VStack>

          <Separator />

          <HStack justify="flex-end">
            <ButtonGroup>
              <Show when={isNotNil(props.deliveryNote)}>
                <FormButton
                  control={control}
                  type="reset"
                  variant="secondary"
                  title={i18n.t('general.labels.discard')}
                  onClick={props.onClose}
                  data-testid={suffixTestId('actions.discard', props)}
                />
              </Show>
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={i18n.t('general.labels.saveChanges')}
                data-testid={suffixTestId('actions.submit', props)}
              />
            </ButtonGroup>
          </HStack>
        </>
      )}
    </Form>
  );
}
